import React, { useState, useEffect } from "react";
import { useCampaign } from "../../contexts/CampaignContext";
import "./CreateCampaign.css";

const SESSION_KEY = "campaign_budget_form";

const CampaignBudget = ({ onNext, onBack }) => {
  // 使用Context
  const { state, utils } = useCampaign();
  const [selectedBudget, setSelectedBudget] = useState("starter");
  const [budgetType, setBudgetType] = useState("total"); // 'daily' or 'total'
  const [expandedFaq, setExpandedFaq] = useState(null);
  const [isLaunching, setIsLaunching] = useState(false);

  // 在组件加载时打印所有准备发送给后端的数据
  useEffect(() => {
    const printCampaignData = () => {
      try {
        // 获取 Campaign Info 数据
        const campaignInfoData = JSON.parse(sessionStorage.getItem('campaign_info_form') || '{}');
        
        // 获取 Audience Content 数据
        const audienceContentData = JSON.parse(sessionStorage.getItem('audience_content_form') || '{}');
        
        // 获取用户ID和广告主ID
        const userId = sessionStorage.getItem('userId') || sessionStorage.getItem('user_id');
        const advertiserId = sessionStorage.getItem('advertiserId') || sessionStorage.getItem('advertiser_id');
        
        // 生成活动开始和结束日期
        const today = new Date();
        const startDate = today.toISOString().split('T')[0];
        const endDateObj = new Date(today);
        endDateObj.setMonth(endDateObj.getMonth() + 2);
        const endDate = endDateObj.toISOString().split('T')[0];
        
        // 获取当前 Budget 数据
        const budgetData = {
          selectedBudget,
          budgetType,
          budgetAmount: getSelectedBudgetData()?.amount || 2000,
          impressions: calculateMetrics().rawImpressions,
          engagements: calculateMetrics().rawEngagements,
          influencerRange: calculateMetrics().influencerRangeArray,
          target_views: calculateMetrics().views
        };

        // 准备完整的活动数据
        const fullCampaignData = {
          user_id: userId,
          advertiser_id: advertiserId,
          start_date: startDate,
          end_date: endDate,
          campaignInfo: campaignInfoData,
          audienceContent: audienceContentData,
          budgetData: budgetData
        };

        // ===== 整洁的API调用参数预览 =====
        console.log('🚀 ===== LAUNCH CAMPAIGN - API PARAMETERS PREVIEW =====');
        
        // API 1: POST /campaign
        const createCampaignData = {
          advertiser_id: advertiserId,
          name: campaignInfoData.campaignName,
          goal: audienceContentData.selectedGoal,
          budget: Math.floor(budgetData.budgetAmount),
          target_views: Math.floor(budgetData.target_views || 45000),
          status: "active",
          start_date: startDate,
          end_date: endDate
        };
        
        console.log('📤 API 1: POST /campaign');
        console.log(createCampaignData);
        
        // API 2: POST /campaign-extras
        const extrasData = {
          campaign_id: 'WILL_BE_SET_AFTER_API_1',
          extras_json: {
            // Campaign Info 数据
            productDescription: campaignInfoData.productDescription,
            landingPage: campaignInfoData.landingPage,
            files: campaignInfoData.files,
            
            // Audience Content 数据
            selectedPriority: audienceContentData.selectedPriority,
            selectedManagement: audienceContentData.selectedManagement,
            avgVideoImpressionType: audienceContentData.avgVideoImpressionType,
            ...(audienceContentData.avgVideoImpressionType === "custom" && {
              customAvgVideoImpression: audienceContentData.customAvgVideoImpression
            }),
            followerRangeType: audienceContentData.followerRangeType,
            ...(audienceContentData.followerRangeType === "custom" && {
              customFollowerMin: audienceContentData.customFollowerMin,
              customFollowerMax: audienceContentData.customFollowerMax
            }),
            ageRangeType: audienceContentData.ageRangeType,
            ...(audienceContentData.ageRangeType === "customized" && {
              selectedAgeGroups: audienceContentData.selectedAgeGroups
            }),
            genderType: audienceContentData.genderType,
            ...(audienceContentData.genderType === "customized" && {
              selectedGenders: audienceContentData.selectedGenders
            }),
            selectedRegions: audienceContentData.selectedRegions,
            audienceCommunityType: audienceContentData.audienceCommunityType,
            ...(audienceContentData.audienceCommunityType === "customized" && {
              selectedCommunities: audienceContentData.selectedCommunities
            }),
            audienceIndustryType: audienceContentData.audienceIndustryType,
            ...(audienceContentData.audienceIndustryType === "customized" && {
              selectedIndustries: audienceContentData.selectedIndustries
            }),
            selectedInterests: audienceContentData.selectedInterests,
            selectedLanguages: audienceContentData.selectedLanguages,
            videoRequirements: audienceContentData.videoRequirements,
            selectedRequirements: audienceContentData.selectedRequirements,
            audience: audienceContentData.audiencePopulation,
            selectedBudget: budgetData.selectedBudget,
            budgetType: budgetData.budgetType,
            impressions: budgetData.impressions,
            engagements: budgetData.engagements,
            influencerRange: budgetData.influencerRange,
            user_id: userId
          }
        };
        
        console.log('📤 API 2: POST /campaign-extras');
        console.log(extrasData);
        
        // API 3: POST /file (如果有文件)
        const filesFromContext = utils.getValidFiles();
        if (filesFromContext.length > 0) {
          console.log('📤 API 3: POST /file');
          console.log('Files to upload:', filesFromContext.map(f => ({
            name: f.name,
            type: f.type,
            size: f.size
          })));
        } else {
          console.log('📤 API 3: POST /file - No files to upload');
        }
        
        console.log('🚀 ===== END API PARAMETERS PREVIEW =====');
      } catch (error) {
        console.error('❌ Error printing campaign data:', error);
      }
    };

    // 延迟执行以确保所有数据都已加载
    const timer = setTimeout(printCampaignData, 1000);
    return () => clearTimeout(timer);
  }, [selectedBudget, budgetType]); // 当预算选择改变时重新打印

  const budgetTiers = [
    {
      id: "starter",
      name: "Starter",
      amount: 2000,
      description: "Test a campaign",
    },
    {
      id: "basic",
      name: "Basic",
      amount: 3000,
      description: "Small-scale reach",
    },
    {
      id: "growth",
      name: "Growth",
      amount: 5000,
      description: "Expanded reach",
    },
    {
      id: "scale",
      name: "Scale",
      amount: 10000,
      description: "Optimal coverage",
    },
    {
      id: "premium",
      name: "Premium",
      amount: 20000,
      description: "Premium influencer mix",
    },
    {
      id: "enterprise",
      name: "Enterprise",
      amount: 50000,
      description: "Maximum reach and impact",
    },
  ];

  const smartFeatures = [
    {
      icon: (
        <svg
          width="20"
          height="20"
          viewBox="0 0 24 24"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M12 5C12.0013 4.60003 11.9224 4.20396 11.7683 3.8349C11.6141 3.46583 11.3877 3.13133 11.1024 2.85104C10.817 2.57076 10.4785 2.35037 10.1067 2.20283C9.73499 2.05529 9.33746 1.98358 8.93758 1.99191C8.53769 2.00025 8.14351 2.08846 7.77822 2.25137C7.41293 2.41427 7.0839 2.64858 6.81049 2.9405C6.53707 3.23243 6.32479 3.57608 6.18613 3.95125C6.04748 4.32642 5.98524 4.72553 6.00308 5.1251C5.41528 5.27624 4.86959 5.55915 4.40731 5.95241C3.94504 6.34567 3.57832 6.83897 3.33493 7.39495C3.09153 7.95092 2.97784 8.55499 3.00247 9.16141C3.0271 9.76783 3.1894 10.3607 3.47708 10.8951C2.97126 11.306 2.57351 11.8343 2.31842 12.434C2.06334 13.0337 1.95864 13.6867 2.01345 14.3361C2.06825 14.9855 2.2809 15.6117 2.63289 16.1601C2.98488 16.7086 3.46555 17.1628 4.03308 17.4831C3.963 18.0253 4.00482 18.5762 4.15597 19.1016C4.30712 19.6271 4.56438 20.1159 4.91187 20.5381C5.25936 20.9602 5.68969 21.3066 6.1763 21.5559C6.66291 21.8051 7.19546 21.952 7.74106 21.9874C8.28666 22.0228 8.83372 21.946 9.34847 21.7617C9.86322 21.5774 10.3347 21.2895 10.7338 20.9159C11.133 20.5422 11.4513 20.0907 11.669 19.5892C11.8868 19.0877 11.9995 18.5468 12.0001 18V5Z"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      ),
      title: "Budget Optimization",
      description:
        "Optimize your budget allocation across influencers for maximum ROI",
    },
    {
      icon: (
        <svg
          width="20"
          height="20"
          viewBox="0 0 24 24"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <circle
            cx="12"
            cy="12"
            r="10"
            stroke="currentColor"
            strokeWidth="2"
          />
          <circle cx="12" cy="12" r="6" stroke="currentColor" strokeWidth="2" />
          <circle cx="12" cy="12" r="2" stroke="currentColor" strokeWidth="2" />
        </svg>
      ),
      title: "Influencer Matching",
      description:
        "Match with the most relevant influencers for your brand and campaign goals",
    },
    {
      icon: (
        <svg
          width="20"
          height="20"
          viewBox="0 0 24 24"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M4 14C4.43283 14.0006 4.85729 14.1476 5.20702 14.4169C5.55674 14.6863 5.8083 15.0623 5.91661 15.4916C6.02493 15.9209 5.99336 16.3806 5.82556 16.7926C5.65777 17.2047 5.36284 17.5469 4.98206 17.77L3.08206 18.87"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      ),
      title: "Processing Priority",
      description:
        "Get your campaign processed and launched faster with priority queue",
    },
    {
      icon: (
        <svg
          width="20"
          height="20"
          viewBox="0 0 24 24"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M12 8V4H8"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M18 8H6C4.89543 8 4 8.89543 4 10V18C4 19.1046 4.89543 20 6 20H18C19.1046 20 20 19.1046 20 18V10C20 8.89543 19.1046 8 18 8Z"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      ),
      title: "Price Negotiation",
      description:
        "Our AI negotiates the best rates with influencers on your behalf",
    },
    {
      icon: (
        <svg
          width="20"
          height="20"
          viewBox="0 0 24 24"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M20 13C20 18 17 20.5 12.96 21.95C12.7421 22.0238 12.5055 22.0203 12.29 21.94C8.12 20.5 4.62 18 4.62 13V6C4.62 5.735 4.725 5.48 4.912 5.293C5.1 5.105 5.355 5 5.62 5C7.62 5 10.12 3.8 11.86 2.28C12.072 2.099 12.341 2 12.62 2C12.899 2 13.168 2.099 13.38 2.28C15.13 3.81 17.62 5 19.62 5C19.885 5 20.14 5.105 20.327 5.293C20.515 5.48 20.62 5.735 20.62 6V13Z"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      ),
      title: "Brand Safety Check",
      description:
        "Advanced content screening to ensure brand safety and alignment",
    },
    {
      icon: (
        <svg
          width="20"
          height="20"
          viewBox="0 0 24 24"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M3 3V19C3 19.5304 3.21071 20.0391 3.58579 20.4142C3.96086 20.7893 4.46957 21 5 21H21"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M18 17V9"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M13 17V5"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M8 17V14"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      ),
      title: "Competitor Analysis",
      description:
        "Analyze competitor campaigns and performance to inform your strategy",
    },
  ];

  const additionalFeatures = [
    {
      icon: (
        <svg
          width="20"
          height="20"
          viewBox="0 0 24 24"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M3 3V19C3 19.5304 3.21071 20.0391 3.58579 20.4142C3.96086 20.7893 4.46957 21 5 21H21"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M19 9L14 14L10 10L7 13"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      ),
      title: "Performance Analytics",
      description: "Detailed performance analytics with actionable insights",
    },
    {
      icon: (
        <svg
          width="20"
          height="20"
          viewBox="0 0 24 24"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M21 15C21 15.5304 20.7893 16.0391 20.4142 16.4142C20.0391 16.7893 19.5304 17 19 17H7L3 21V5C3 4.46957 3.21071 3.96086 3.58579 3.58579C3.96086 3.21071 4.46957 3 5 3H19C19.5304 3 20.0391 3.21071 20.4142 3.58579C20.7893 3.96086 21 4.46957 21 5V15Z"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      ),
      title: "Dedicated Support",
      description: "Get personalized support from a dedicated campaign manager",
    },
    {
      icon: (
        <svg
          width="20"
          height="20"
          viewBox="0 0 24 24"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M15.477 12.89L16.992 21.416C17.009 21.5164 16.9949 21.6196 16.9516 21.7118C16.9084 21.8039 16.838 21.8807 16.7499 21.9318C16.6619 21.9829 16.5603 22.0059 16.4588 21.9977C16.3573 21.9895 16.2607 21.9506 16.182 21.886L12.602 19.199C12.4292 19.0699 12.2192 19.0001 12.0035 19.0001C11.7878 19.0001 11.5778 19.0699 11.405 19.199L7.819 21.885C7.74032 21.9494 7.64386 21.9884 7.54249 21.9966C7.44112 22.0048 7.33967 21.9818 7.25166 21.9309C7.16365 21.9799 7.09327 21.8033 7.04991 21.7113C7.00656 21.9193 6.99228 21.8163 7.009 21.716L8.523 12.89"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M12 14C15.3137 14 18 11.3137 18 8C18 4.68629 15.3137 2 12 2C8.68629 2 6 4.68629 6 8C6 11.3137 8.68629 14 12 14Z"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      ),
      title: "Performance Guarantee",
      description:
        "Money-back guarantee if campaign doesn't meet performance targets",
    },
  ];

  const faqItems = [
    {
      question: "How many influencers will I work with?",
      answer:
        "The number of influencers depends on your budget and campaign goals. Our AI optimization will select the optimal number for maximum reach and engagement.",
    },
    {
      question: "What type of influencers will promote my brand?",
      answer:
        "We work with a diverse range of influencers across all major platforms, from micro-influencers to celebrities, ensuring the best fit for your brand and audience.",
    },
    {
      question: "How is my budget allocated?",
      answer:
        "Our AI dynamically allocates your budget across influencers based on performance metrics, audience match, and content quality for maximum ROI.",
    },
    {
      question: "What happens if I'm not satisfied with the results?",
      answer:
        "We offer a performance guarantee. If we don't meet the minimum guaranteed metrics, you'll receive a partial refund based on our performance formula.",
    },
    {
      question: "How long does it take to launch my campaign?",
      answer:
        "Most campaigns launch within 3-5 business days. Premium and Enterprise plans include priority processing for faster launches.",
    },
    {
      question: "What metrics will I be able to track?",
      answer:
        "You'll have access to comprehensive analytics including impressions, engagements, reach, conversion tracking, and detailed performance reports.",
    },
  ];

  const getSelectedBudgetData = () => {
    return budgetTiers.find((tier) => tier.id === selectedBudget);
  };

  const calculateMetrics = () => {
    const budget = getSelectedBudgetData()?.amount || 2000;

    // Base metrics for 2000 USD = 90000 views
    const baseViews = 90000;
    const baseImpressions = 90000; // 与views相同
    const baseEngagements = 2152;

    // Scale factor based on budget (relative to 2000 USD)
    const scaleFactor = budget / 2000;

    // Define specific influencer ranges for each budget tier
    const influencerRanges = {
      2000: [3, 10],
      3000: [6, 20],
      5000: [10, 40],
      10000: [30, 85],
      20000: [45, 100],
      50000: [80, 200]
    };

    // Get influencer range for current budget
    const [minInfluencers, maxInfluencers] = influencerRanges[budget] || [3, 10];

    // Calculate scaled metrics (绝对整数)
    const views = Math.floor(baseViews * scaleFactor);
    const impressions = Math.floor(baseImpressions * scaleFactor);
    const engagements = Math.floor(baseEngagements * scaleFactor);
    const costPerImpression = (budget / impressions).toFixed(3);
    const costPerEngagement = (budget / engagements).toFixed(2);
    const engagementRate = ((engagements / impressions) * 100).toFixed(1);

    return {
      views: views, // 绝对整数浏览量
      impressions: impressions.toLocaleString(),
      rawImpressions: impressions,
      engagements: engagements.toLocaleString(),
      rawEngagements: engagements,
      costPerImpression,
      costPerEngagement,
      engagementRate,
      influencerRange: `${minInfluencers} - ${maxInfluencers}`,
      influencerRangeArray: [minInfluencers, maxInfluencers],
      dailySpend: Math.floor(budget / 12), // Assuming 12-day campaign
    };
  };

  const toggleFaq = (index) => {
    setExpandedFaq(expandedFaq === index ? null : index);
  };

  // Launch Campaign API 调用函数
  const launchCampaignAPI = async (campaignData) => {
    try {
      // 第一步：创建 Campaign
      const createCampaignData = {
        advertiser_id: campaignData.advertiser_id,
        name: campaignData.campaignInfo.campaignName,
        goal: campaignData.audienceContent.selectedGoal,
        budget: Math.floor(campaignData.budgetData.budgetAmount), // 绝对整数
        target_views: Math.floor(campaignData.budgetData.target_views || 45000), // 绝对整数
        status: "active",
        start_date: campaignData.start_date,
        end_date: campaignData.end_date
      };

      console.log('📤 ===== API CALL 1: POST /campaign =====');
      console.log('📤 Campaign Creation Data:', createCampaignData);
      console.log('🌐 API URL:', `${process.env.REACT_APP_API_BASE_URL}/campaign`);

      const createResponse = await fetch(`${process.env.REACT_APP_API_BASE_URL}/campaign`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(createCampaignData)
      });

      const createResult = await createResponse.json();
      console.log('✅ Campaign Creation Response:', createResult);
      console.log('✅ ===== END API CALL 1 =====');

      // 获取 campaign_id
      const campaignId = createResult.campaign_id || createResult.id;

      if (!campaignId) {
        throw new Error('未能获取到campaign_id');
      }

      // 第二步：上传 Campaign Extras
      const extrasData = {
        campaign_id: campaignId,
        extras_json: {
          // Campaign Info 数据
          productDescription: campaignData.campaignInfo.productDescription,
          landingPage: campaignData.campaignInfo.landingPage,
          files: campaignData.campaignInfo.files,
          
          // Audience Content 数据
          selectedPriority: campaignData.audienceContent.selectedPriority,
          selectedManagement: campaignData.audienceContent.selectedManagement,
          avgVideoImpressionType: campaignData.audienceContent.avgVideoImpressionType,
          // 只有当不是AI优化时才提交自定义数据
          ...(campaignData.audienceContent.avgVideoImpressionType === "custom" && {
            customAvgVideoImpression: campaignData.audienceContent.customAvgVideoImpression
          }),
          followerRangeType: campaignData.audienceContent.followerRangeType,
          // 只有当不是AI优化时才提交自定义数据
          ...(campaignData.audienceContent.followerRangeType === "custom" && {
            customFollowerMin: campaignData.audienceContent.customFollowerMin,
            customFollowerMax: campaignData.audienceContent.customFollowerMax
          }),
          ageRangeType: campaignData.audienceContent.ageRangeType,
          // 只有当不是AI优化时才提交选择的年龄组
          ...(campaignData.audienceContent.ageRangeType === "customized" && {
            selectedAgeGroups: campaignData.audienceContent.selectedAgeGroups
          }),
          genderType: campaignData.audienceContent.genderType,
          // 只有当不是AI优化时才提交选择的性别
          ...(campaignData.audienceContent.genderType === "customized" && {
            selectedGenders: campaignData.audienceContent.selectedGenders
          }),
          selectedRegions: campaignData.audienceContent.selectedRegions,
          audienceCommunityType: campaignData.audienceContent.audienceCommunityType,
          // 只有当不是AI优化时才提交选择的社区
          ...(campaignData.audienceContent.audienceCommunityType === "customized" && {
            selectedCommunities: campaignData.audienceContent.selectedCommunities
          }),
          audienceIndustryType: campaignData.audienceContent.audienceIndustryType,
          // 只有当不是AI优化时才提交选择的行业
          ...(campaignData.audienceContent.audienceIndustryType === "customized" && {
            selectedIndustries: campaignData.audienceContent.selectedIndustries
          }),
          selectedInterests: campaignData.audienceContent.selectedInterests,
          selectedLanguages: campaignData.audienceContent.selectedLanguages,
          videoRequirements: campaignData.audienceContent.videoRequirements,
          selectedRequirements: campaignData.audienceContent.selectedRequirements,
          
          // 人口数据
          audience: campaignData.audienceContent.audiencePopulation,
          
          // Budget 数据
          selectedBudget: campaignData.budgetData.selectedBudget,
          budgetType: campaignData.budgetData.budgetType,
          impressions: campaignData.budgetData.impressions,
          engagements: campaignData.budgetData.engagements,
          influencerRange: campaignData.budgetData.influencerRange,
          
          // 用户ID
          user_id: campaignData.user_id
        }
      };

      console.log('📤 ===== API CALL 2: POST /campaign-extras =====');
      console.log('📤 Campaign Extras Data:', extrasData);
      console.log('🌐 API URL:', `${process.env.REACT_APP_API_BASE_URL}/campaign-extras`);

      const extrasResponse = await fetch(`${process.env.REACT_APP_API_BASE_URL}/campaign-extras`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(extrasData)
      });

      const extrasResult = await extrasResponse.text();
      console.log('✅ Campaign Extras Response:', extrasResult);
      console.log('✅ ===== END API CALL 2 =====');

      return {
        campaignResult: createResult,
        extrasResult: extrasResult
      };

    } catch (error) {
      console.error('API调用错误:', error);
      throw error;
    }
  };

  // 文件上传函数
  const uploadCampaignFiles = async (campaignId, files) => {
    if (!files || files.length === 0) {
      console.log('⚠️ No files to upload');
      return;
    }

    const FILE_UPLOAD_URL = `${process.env.REACT_APP_API_BASE_URL}/file`;
    const ASSOCIATE_TYPE = 'campaign';
    const ASSOCIATE_ID = campaignId;

    console.log('=== File Upload API Parameters ===');
    console.log('📡 FILE_UPLOAD_URL:', FILE_UPLOAD_URL);
    console.log('🏷️ ASSOCIATE_TYPE:', ASSOCIATE_TYPE);
    console.log('🆔 ASSOCIATE_ID:', ASSOCIATE_ID);
    console.log('📁 File count:', files.length);
    console.log('📋 File details:', files.map(f => ({ 
      name: f.name, 
      type: f.type, 
      size: f.size,
      isFile: f instanceof File
    })));

    try {
      console.log('🚀 Starting file upload to Campaign:', campaignId);
      
      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        const isFirstFile = i === 0;
        const httpMethod = isFirstFile ? 'POST' : 'PATCH';
        
        console.log(`\n=== ${isFirstFile ? 'Uploading' : 'Appending'} file ${i + 1}/${files.length} ===`);
        console.log('🔄 HTTP method:', httpMethod);
        console.log('📄 File object details:', {
          name: file.name,
          type: file.type,
          size: file.size,
          lastModified: file.lastModified,
          isFile: file instanceof File
        });

        const formData = new FormData();
        formData.append('file', file);
        formData.append('associate_type', ASSOCIATE_TYPE);
        formData.append('associate_id', ASSOCIATE_ID);

        // 打印FormData内容（用于调试）
        console.log('📦 FormData contents:');
        for (let [key, value] of formData.entries()) {
          if (value instanceof File) {
            console.log(`  ${key}:`, {
              name: value.name,
              type: value.type,
              size: value.size,
              isFile: true
            });
          } else {
            console.log(`  ${key}:`, value);
          }
        }

        const uploadResponse = await fetch(FILE_UPLOAD_URL, {
          method: httpMethod,
          body: formData
        });

        const uploadResult = await uploadResponse.json();
        console.log(`📤 File "${file.name}" ${isFirstFile ? 'upload' : 'append'} result:`, {
          statusCode: uploadResponse.status,
          response: uploadResult
        });
      }

      console.log('✅ All files uploaded successfully');
    } catch (error) {
      console.error('❌ Error occurred during file upload:', error);
    }
  };

  const handleNext = async () => {
    // 防止重复点击
    if (isLaunching) {
      return;
    }
    
    setIsLaunching(true);
    
    try {
      const metrics = calculateMetrics();
      const selectedBudgetData = getSelectedBudgetData();
      
      // 获取 Campaign Info 数据
      const campaignInfoData = JSON.parse(sessionStorage.getItem('campaign_info_form') || '{}');
      
      // 从Context获取文件对象
      const campaignFiles = utils.getValidFiles();
      
      console.log('=== File Upload Debug Info ===');
      console.log('📁 Files in Context:', state.campaignInfo.files.length);
      console.log('✅ Valid files count:', campaignFiles.length);
      console.log('📋 File objects from Context:', campaignFiles.map(f => ({
        name: f.name,
        type: f.type,
        size: f.size,
        isFile: f instanceof File
      })));
      console.log('ℹ️ campaignInfoData.filesInfo:', campaignInfoData.filesInfo);
      
      // 获取 Audience Content 数据
      const audienceContentData = JSON.parse(sessionStorage.getItem('audience_content_form') || '{}');
      
      // 获取用户ID和广告主ID
      const userId = sessionStorage.getItem('userId') || sessionStorage.getItem('user_id');
      const advertiserId = sessionStorage.getItem('advertiserId') || sessionStorage.getItem('advertiser_id');
      
      // 生成活动开始和结束日期
      const today = new Date();
      const startDate = today.toISOString().split('T')[0]; // 格式: "2024-05-01"
      
      // 计算两个月后的日期
      const endDateObj = new Date(today);
      endDateObj.setMonth(endDateObj.getMonth() + 2);
      const endDate = endDateObj.toISOString().split('T')[0]; // 格式: "2024-07-01"
      
      // 获取当前 Budget 数据
      const budgetData = {
        selectedBudget,
        budgetType,
        budgetAmount: selectedBudgetData?.amount || 2000,
        impressions: metrics.rawImpressions,
        engagements: metrics.rawEngagements,
        influencerRange: metrics.influencerRangeArray,
        target_views: metrics.views // 使用绝对整数浏览量
      };
      
      // 保存Budget数据到sessionStorage
      sessionStorage.setItem(
        SESSION_KEY,
        JSON.stringify({
          ...budgetData,
          advertiserId
        })
      );

      // 准备完整的活动数据
      const fullCampaignData = {
        user_id: userId,
        advertiser_id: advertiserId,
        start_date: startDate,
        end_date: endDate,
        campaignInfo: campaignInfoData,
        audienceContent: audienceContentData,
        budgetData: budgetData
      };

      // ===== 详细的API调用和数据日志 =====
      console.log('🚀 ===== LAUNCH CAMPAIGN - API CALLS & DATA =====');
      console.log('📅 Campaign Dates:');
      console.log('  - Start Date:', startDate);
      console.log('  - End Date:', endDate);
      console.log('👤 User Information:');
      console.log('  - User ID:', userId);
      console.log('  - Advertiser ID:', advertiserId);
      
      console.log('📋 Campaign Info Data:');
      console.log('  - Campaign Name:', campaignInfoData.campaignName);
      console.log('  - Product Description:', campaignInfoData.productDescription);
      console.log('  - Landing Page:', campaignInfoData.landingPage);
      console.log('  - Files Info:', campaignInfoData.filesInfo);
      console.log('  - Full Campaign Info:', campaignInfoData);
      
      console.log('🎯 Audience Content Data:');
      console.log('  - Selected Goal:', audienceContentData.selectedGoal);
      console.log('  - Selected Priority:', audienceContentData.selectedPriority);
      console.log('  - Selected Management:', audienceContentData.selectedManagement);
      console.log('  - Video Requirements:', audienceContentData.videoRequirements);
      console.log('  - Selected Languages:', audienceContentData.selectedLanguages);
      console.log('  - Selected Interests:', audienceContentData.selectedInterests);
      console.log('  - Selected Regions:', audienceContentData.selectedRegions);
      console.log('  - Audience Population:', audienceContentData.audiencePopulation);
      console.log('  - Age Range Type:', audienceContentData.ageRangeType);
      console.log('  - Selected Age Groups:', audienceContentData.selectedAgeGroups);
      console.log('  - Gender Type:', audienceContentData.genderType);
      console.log('  - Selected Genders:', audienceContentData.selectedGenders);
      console.log('  - Avg Video Impression Type:', audienceContentData.avgVideoImpressionType);
      console.log('  - Custom Avg Video Impression:', audienceContentData.customAvgVideoImpression);
      console.log('  - Follower Range Type:', audienceContentData.followerRangeType);
      console.log('  - Custom Follower Min:', audienceContentData.customFollowerMin);
      console.log('  - Custom Follower Max:', audienceContentData.customFollowerMax);
      console.log('  - Selected Communities:', audienceContentData.selectedCommunities);
      console.log('  - Selected Industries:', audienceContentData.selectedIndustries);
      console.log('  - Selected Requirements:', audienceContentData.selectedRequirements);
      console.log('  - Full Audience Content:', audienceContentData);
      
      console.log('💰 Budget Data:');
      console.log('  - Selected Budget:', budgetData.selectedBudget);
      console.log('  - Budget Type:', budgetData.budgetType);
      console.log('  - Budget Amount:', budgetData.budgetAmount);
      console.log('  - Impressions:', budgetData.impressions);
      console.log('  - Engagements:', budgetData.engagements);
      console.log('  - Influencer Range:', budgetData.influencerRange);
      console.log('  - Target Views:', budgetData.target_views);
      console.log('  - Full Budget Data:', budgetData);
      
      console.log('📁 File Upload Information:');
      console.log('  - Files in Context:', campaignFiles.length);
      console.log('  - File Details:', campaignFiles.map(f => ({
        name: f.name,
        type: f.type,
        size: f.size,
        isFile: f instanceof File
      })));
      
      console.log('🌐 API Endpoints to be called:');
      console.log('  1. POST /campaign - Create campaign with basic campaign data');
      console.log('  2. POST /campaign-extras - Upload detailed campaign extras data');
      console.log('  3. POST /file - Upload campaign files (if any)');
      
      console.log('📤 Full Campaign Data to be sent to API:');
      console.log(fullCampaignData);
      console.log('🚀 ===== END OF LAUNCH CAMPAIGN DATA =====');

      // 调用API创建Campaign
      console.log('🔄 ===== STARTING API CALLS =====');
      console.log('🔄 Calling launchCampaignAPI...');
      const campaignApiResult = await launchCampaignAPI(fullCampaignData);
      console.log('✅ ===== API CALLS COMPLETED =====');
      console.log('✅ Campaign API Result:', campaignApiResult);
      
      // 获取创建的 Campaign ID
      const campaignId = campaignApiResult.campaignResult.campaign_id || campaignApiResult.campaignResult.id;
      console.log('🆔 Created Campaign ID:', campaignId);
      
      // 上传文件到创建的Campaign
      if (campaignId && campaignFiles.length > 0) {
        console.log('📁 ===== API CALL 3: POST /file =====');
        console.log('📁 Uploading files to campaign...');
        console.log('  - Campaign ID:', campaignId);
        console.log('  - Files to upload:', campaignFiles.length);
        console.log('  - File details:', campaignFiles.map(f => ({
          name: f.name,
          type: f.type,
          size: f.size
        })));
        
        await uploadCampaignFiles(campaignId, campaignFiles);
        console.log('✅ Files uploaded successfully');
        console.log('✅ ===== END API CALL 3 =====');
        // 文件上传成功，继续跳转
        console.log('✅ Campaign created successfully, redirecting to homepage...');
        window.location.href = '/';
      } else if (campaignId && campaignInfoData.filesInfo && campaignInfoData.filesInfo.length > 0) {
        console.warn('⚠️ File information found but no actual file objects available.');
        console.warn('💡 This usually happens after page navigation when file objects are lost.');
        console.warn('🔧 Solution: Please go back to Campaign Info page and re-select files.');
        console.warn('📋 Files that need to be re-selected:', campaignInfoData.filesInfo.map(f => f.name));
        
        // 弹出友好提示，但不跳转，让用户处理文件问题
        alert(`${campaignInfoData.filesInfo.length} files detected for upload, but file objects are lost.\n\nPlease go back to Campaign Info page and re-select the following files:\n${campaignInfoData.filesInfo.map(f => `• ${f.name}`).join('\n')}\n\nThen click Launch Campaign again.`);
        setIsLaunching(false);
        return; // 停止执行，不跳转
      } else {
        // 没有文件需要上传，直接跳转
        console.log('ℹ️ No files to upload, proceeding to redirect...');
        console.log('✅ Campaign created successfully (no files to upload), redirecting to homepage...');
        window.location.href = '/';
      }

      // 保持原有的 onNext 调用以防其他逻辑需要
      if (onNext) {
        onNext();
      }
    } catch (error) {
      console.error('❌ ===== LAUNCH CAMPAIGN FAILED =====');
      console.error('❌ Error Details:', error);
      console.error('❌ Error Message:', error.message);
      console.error('❌ Error Stack:', error.stack);
      console.error('❌ ===== END ERROR LOG =====');
      setIsLaunching(false);
      // 可以在这里添加用户友好的错误提示
      alert('Campaign launch failed. Please try again.');
    }
  };

  const handleBack = () => {
    if (onBack) {
      onBack();
    }
  };

  const metrics = calculateMetrics();
  const selectedBudgetData = getSelectedBudgetData();

  // 恢复
  useEffect(() => {
    const saved = sessionStorage.getItem(SESSION_KEY);
    if (saved) {
      try {
        const data = JSON.parse(saved);
        if (data.selectedBudget) setSelectedBudget(data.selectedBudget);
        if (data.budgetType) setBudgetType(data.budgetType);
      } catch {}
    }
  }, []);

  // 持久化
  useEffect(() => {
    sessionStorage.setItem(
      SESSION_KEY,
      JSON.stringify({
        selectedBudget,
        budgetType,
      })
    );
  }, [selectedBudget, budgetType]);

  return (
    <form className="campaign-form">
      <header className="form-header">
        <div>
          <h2 className="form-title">Campaign Budget</h2>
          <p className="form-subtitle">
            Set your campaign investment and review performance guarantees
          </p>
        </div>
        <span className="step-indicator">Step 3 of 3</span>
      </header>

      {/* Main Content Layout */}
      <div className="budget-main-content">
        {/* Left Column - Budget Selection */}
        <div className="budget-left-column">
          <section className="budget-selection-section">
            <div className="section-title-group">
              <h3 className="section-title">Campaign Budget</h3>
              <div className="budget-type-selector">
                <button
                  type="button"
                  className={`budget-type-btn ${budgetType === "daily" ? "" : "active"}`}
                  onClick={() => setBudgetType("total")}
                >
                  Total
                </button>
                <button
                  type="button"
                  className={`budget-type-btn ${budgetType === "daily" ? "active" : ""}`}
                  onClick={() => setBudgetType("daily")}
                >
                  Daily
                </button>
                <button type="button" className="edit-btn">
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M12 3H5C4.46957 3 3.96086 3.21071 3.58579 3.58579C3.21071 3.96086 3 4.46957 3 5V19C3 19.5304 3.21071 20.0391 3.58579 20.4142C3.96086 20.7893 4.46957 21 5 21H19C19.5304 21 20.0391 20.7893 20.4142 20.4142C20.7893 20.0391 21 19.5304 21 19V12"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M18.997 2.925C19.3948 2.52722 19.9344 2.30372 20.497 2.30372C21.0596 2.30372 21.5992 2.52722 21.997 2.925C22.3948 3.32278 22.6183 3.86235 22.6183 4.425C22.6183 4.98765 22.3948 5.52722 21.997 5.925L12.984 14.938C12.7465 15.1756 12.4532 15.3492 12.131 15.444L9.258 16.284"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                  Edit
                </button>
              </div>
            </div>

            <div className="current-budget-display">
              <span className="budget-amount">
                ${selectedBudgetData?.amount.toLocaleString()}
              </span>
              <span className="budget-label">total budget</span>
            </div>
            <p className="daily-spend-info">
              Average daily spend: ${metrics.dailySpend}
            </p>

            <div className="influencer-count">
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M16 21V19C16 17.9391 15.5786 16.9217 14.8284 16.1716C14.0783 15.4214 13.0609 15 12 15H6C4.93913 15 3.92172 15.4214 3.17157 16.1716C2.42143 16.9217 2 17.9391 2 19V21"
                  stroke="#1a73e8"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M9 11C11.2091 11 13 9.20914 13 7C13 4.79086 11.2091 3 9 3C6.79086 3 5 4.79086 5 7C5 9.20914 6.79086 11 9 11Z"
                  stroke="#1a73e8"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M22 21V19C21.9993 18.1137 21.7044 17.2528 21.1614 16.5523C20.6184 15.8519 19.8581 15.3516 19 15.13"
                  stroke="#1a73e8"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M16 3.13C16.8604 3.35031 17.623 3.85071 18.1676 4.55232C18.7122 5.25392 19.0078 6.11683 19.0078 7.005C19.0078 7.89317 18.7122 8.75608 18.1676 9.45768C17.623 10.1593 16.8604 10.6597 16 10.88"
                  stroke="#1a73e8"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              <span>{metrics.influencerRange} influencers</span>
            </div>

            <div className="recommended-budgets-section">
              <h4 className="recommended-title">Recommended Budgets</h4>
              <p className="recommended-subtitle">
                Select a preset budget tier
              </p>

              <div className="budget-tiers-grid">
                {budgetTiers.map((tier) => (
                  <div
                    key={tier.id}
                    className={`budget-tier ${selectedBudget === tier.id ? "selected" : ""}`}
                    onClick={() => setSelectedBudget(tier.id)}
                  >
                    <div className="tier-header">
                      <span className="tier-name">{tier.name}</span>
                      <span className="tier-amount">
                        ${tier.amount.toLocaleString()}
                      </span>
                    </div>
                    <p className="tier-description">{tier.description}</p>
                  </div>
                ))}
              </div>
            </div>

            <div className="ai-optimization-info">
              <div className="optimization-icon">
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <circle
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="#1a73e8"
                    strokeWidth="2"
                  />
                  <path
                    d="M12 16V12"
                    stroke="#1a73e8"
                    strokeWidth="2"
                    strokeLinecap="round"
                  />
                  <path
                    d="M12 8H12.01"
                    stroke="#1a73e8"
                    strokeWidth="2"
                    strokeLinecap="round"
                  />
                </svg>
              </div>
              <p className="optimization-text">
                Our AI optimization engine dynamically allocates your budget
                across influencers based on their performance metrics, audience
                match, and content quality. This intelligent distribution
                ensures maximum ROI by investing more in high-performing
                partnerships.
              </p>
            </div>
          </section>

          <section className="smart-features-section">
            <div className="smart-features-header">
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M21 10.5V19C21 19.5304 20.7893 20.0391 20.4142 20.4142C20.0391 20.7893 19.5304 21 19 21H5C4.46957 21 3.96086 20.7893 3.58579 20.4142C3.21071 20.0391 3 19.5304 3 19V5C3 4.46957 3.21071 3.96086 3.58579 3.58579C3.96086 3.21071 4.46957 3 5 3H17.5"
                  stroke="#1a73e8"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M9 11L12 14L22 4"
                  stroke="#1a73e8"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              <h4 className="smart-features-title">Smart Mode Features</h4>
            </div>

            <div className="smart-features-grid">
              {smartFeatures.map((feature, index) => (
                <div key={index} className="smart-feature-card">
                  <div className="feature-icon-container">
                    <div className="feature-icon">{feature.icon}</div>
                  </div>
                  <div className="feature-content">
                    <h5 className="feature-title">{feature.title}</h5>
                    <p className="feature-description">{feature.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </section>
        </div>

        {/* Right Column - Performance Guarantees */}
        <div className="budget-right-column">
          <section className="performance-guarantees-section">
            <div className="guarantees-header">
              <div className="guarantee-icon-large">
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M15.477 12.89L16.992 21.416C17.009 21.5164 16.9949 21.6196 16.9516 21.7118C16.9084 21.8039 16.838 21.8807 16.7499 21.9318C16.6619 21.9829 16.5603 22.0059 16.4588 21.9977C16.3573 21.9895 16.2607 21.9506 16.182 21.886L12.602 19.199C12.4292 19.0699 12.2192 19.0001 12.0035 19.0001C11.7878 19.0001 11.5778 19.0699 11.405 19.199L7.819 21.885C7.74032 21.9494 7.64386 21.9884 7.54249 21.9966C7.44112 22.0048 7.33967 21.9818 7.25166 21.9309C7.16365 21.9799 7.09327 21.8033 7.04991 21.7113C7.00656 21.9193 6.99228 21.8163 7.009 21.716L8.523 12.89"
                    stroke="white"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M12 14C15.3137 14 18 11.3137 18 8C18 4.68629 15.3137 2 12 2C8.68629 2 6 4.68629 6 8C6 11.3137 8.68629 14 12 14Z"
                    stroke="white"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </div>
              <div className="guarantees-title-group">
                <h3 className="guarantees-title">Guaranteed Performance</h3>
                <div className="powered-by-badge">Powered by Janney AI</div>
                <p className="guarantees-subtitle">
                  AI-powered optimization with guaranteed results
                </p>
              </div>
            </div>

            <div className="impressions-guarantee">
              <div className="guarantee-header">
                <div className="guarantee-icon">
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M16 21V19C16 17.9391 15.5786 16.9217 14.8284 16.1716C14.0783 15.4214 13.0609 15 12 15H6C4.93913 15 3.92172 15.4214 3.17157 16.1716C2.42143 16.9217 2 17.9391 2 19V21"
                      stroke="#1a73e8"
                      strokeWidth="2"
                    />
                    <path
                      d="M9 11C11.2091 11 13 9.20914 13 7C13 4.79086 11.2091 3 9 3C6.79086 3 5 4.79086 5 7C5 9.20914 6.79086 11 9 11Z"
                      stroke="#1a73e8"
                      strokeWidth="2"
                    />
                  </svg>
                </div>
                <div className="guarantee-title-section">
                  <h4 className="guarantee-title">
                    Target Impressions
                  </h4>
                  <button type="button" className="help-icon">
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <circle
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="#737373"
                        strokeWidth="2"
                      />
                      <path
                        d="M9.09 9C9.3251 8.33167 9.78915 7.76811 10.4 7.40913C11.0108 7.05016 11.7289 6.91894 12.4272 7.03871C13.1255 7.15849 13.7588 7.52152 14.2151 8.06353C14.6714 8.60553 14.9211 9.29152 14.92 10C14.92 12 11.92 13 11.92 13"
                        stroke="#737373"
                        strokeWidth="2"
                      />
                      <path d="M12 17H12.01" stroke="#737373" strokeWidth="2" />
                    </svg>
                  </button>
                </div>
              </div>
              <div className="guarantee-value-row">
                <div className="guarantee-value">{metrics.impressions}</div>
                <div className="guarantee-badge">
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M22.423 10.3C22.8797 12.5413 22.5542 14.8715 21.5008 16.9018C20.4475 18.9322 18.7299 20.5401 16.6345 21.4574C14.5391 22.3746 12.1926 22.5458 9.98628 21.9424C7.77995 21.339 5.84717 19.9974 4.51025 18.1415C3.17334 16.2855 2.5131 14.0273 2.63964 11.7434C2.76618 9.45957 3.67186 7.28813 5.20563 5.59121C6.7394 3.89429 8.80856 2.77447 11.068 2.41849C13.3275 2.06251 15.6408 2.4919 17.622 3.63504"
                      stroke="#16A34A"
                      strokeWidth="2"
                    />
                    <path
                      d="M9.622 11.3L12.622 14.3L22.622 4.30005"
                      stroke="#16A34A"
                      strokeWidth="2"
                    />
                  </svg>
                  <span>100% Guaranteed</span>
                </div>
              </div>
              <span className="guarantee-label">impressions</span>
            </div>

            <div className="cost-metrics-section">
              <div className="cost-metric-item">
                <h5 className="cost-metric-title">Max Cost per Impression:</h5>
                <span className="cost-metric-value">
                  ${metrics.costPerImpression}
                </span>
              </div>
              <p className="cost-metric-description">
                Your maximum cost per impression, calculated based on your
                budget and guaranteed reach.
              </p>
            </div>

            <div className="engagements-guarantee">
              <div className="guarantee-header">
                <div className="guarantee-icon">
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path d="M7 10V22" stroke="#1a73e8" strokeWidth="2" />
                    <path
                      d="M15 6.18L14 10.3H19.85C20.1605 10.3 20.4667 10.3723 20.7444 10.5112C21.0221 10.65 21.2637 10.8517 21.45 11.1"
                      stroke="#1a73e8"
                      strokeWidth="2"
                    />
                  </svg>
                </div>
                <div className="guarantee-title-section">
                  <h4 className="guarantee-title">
                    Target Engagements
                  </h4>
                  <button type="button" className="help-icon">
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <circle
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="#737373"
                        strokeWidth="2"
                      />
                      <path
                        d="M9.09 9C9.3251 8.33167 9.78915 7.76811 10.4 7.40913C11.0108 7.05016 11.7289 6.91894 12.4272 7.03871C13.1255 7.15849 13.7588 7.52152 14.2151 8.06353C14.6714 8.60553 14.9211 9.29152 14.92 10C14.92 12 11.92 13 11.92 13"
                        stroke="#737373"
                        strokeWidth="2"
                      />
                      <path d="M12 17H12.01" stroke="#737373" strokeWidth="2" />
                    </svg>
                  </button>
                </div>
              </div>
              <div className="guarantee-value-row">
                <div className="guarantee-value">{metrics.engagements}</div>
                <div className="guarantee-badge">
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M22.423 10.3C22.8797 12.5413 22.5542 14.8715 21.5008 16.9018C20.4475 18.9322 18.7299 20.5401 16.6345 21.4574C14.5391 22.3746 12.1926 22.5458 9.98628 21.9424C7.77995 21.339 5.84717 19.9974 4.51025 18.1415C3.17334 16.2855 2.5131 14.0273 2.63964 11.7434C2.76618 9.45957 3.67186 7.28813 5.20563 5.59121C6.7394 3.89429 8.80856 2.77447 11.068 2.41849C13.3275 2.06251 15.6408 2.4919 17.622 3.63504"
                      stroke="#16A34A"
                      strokeWidth="2"
                    />
                    <path
                      d="M9.622 11.3L12.622 14.3L22.622 4.30005"
                      stroke="#16A34A"
                      strokeWidth="2"
                    />
                  </svg>
                  <span>100% Guaranteed</span>
                </div>
              </div>
              <span className="guarantee-label">engagements</span>
            </div>

            <div className="engagement-metrics-section">
              <div className="metrics-row">
                <div className="metric-item">
                  <span className="metric-label">Engagement Rate:</span>
                  <span className="metric-value">
                    {metrics.engagementRate}%
                  </span>
                </div>
                <div className="industry-comparison">
                  <span className="comparison-badge">+0.5% vs. industry</span>
                </div>
              </div>

              <div className="cost-metric-item">
                <h5 className="cost-metric-title">Max Cost per Engagement:</h5>
                <span className="cost-metric-value">
                  ${metrics.costPerEngagement}
                </span>
              </div>

              <p className="cost-metric-description">
                Engagement rate represents the percentage of viewers who will
                interact with your content through likes, comments, shares, and
                saves.
              </p>
            </div>
          </section>

          <section className="performance-chart-section">
            <h4 className="chart-title">Performance Over Time</h4>
            <div className="optimization-badge">Real-time Optimization</div>

            <div className="chart-container">
              <svg
                width="100%"
                height="200"
                viewBox="0 0 400 200"
                className="performance-chart"
              >
                {/* Grid lines */}
                <defs>
                  <pattern
                    id="grid"
                    width="80"
                    height="40"
                    patternUnits="userSpaceOnUse"
                  >
                    <path
                      d="M 80 0 L 0 0 0 40"
                      fill="none"
                      stroke="#f1f5f9"
                      strokeWidth="1"
                    />
                  </pattern>
                </defs>
                <rect width="100%" height="100%" fill="url(#grid)" />

                {/* Y-axis labels */}
                <text
                  x="15"
                  y="180"
                  fontSize="10"
                  fill="#9CA3AF"
                  textAnchor="middle"
                >
                  0%
                </text>
                <text
                  x="15"
                  y="140"
                  fontSize="10"
                  fill="#9CA3AF"
                  textAnchor="middle"
                >
                  25%
                </text>
                <text
                  x="15"
                  y="100"
                  fontSize="10"
                  fill="#9CA3AF"
                  textAnchor="middle"
                >
                  50%
                </text>
                <text
                  x="15"
                  y="60"
                  fontSize="10"
                  fill="#9CA3AF"
                  textAnchor="middle"
                >
                  75%
                </text>
                <text
                  x="15"
                  y="20"
                  fontSize="10"
                  fill="#9CA3AF"
                  textAnchor="middle"
                >
                  100%
                </text>

                {/* X-axis labels */}
                <text
                  x="40"
                  y="195"
                  fontSize="10"
                  fill="#9CA3AF"
                  textAnchor="middle"
                >
                  0
                </text>
                <text
                  x="100"
                  y="195"
                  fontSize="10"
                  fill="#9CA3AF"
                  textAnchor="middle"
                >
                  02
                </text>
                <text
                  x="160"
                  y="195"
                  fontSize="10"
                  fill="#9CA3AF"
                  textAnchor="middle"
                >
                  012
                </text>
                <text
                  x="220"
                  y="195"
                  fontSize="10"
                  fill="#9CA3AF"
                  textAnchor="middle"
                >
                  018
                </text>
                <text
                  x="280"
                  y="195"
                  fontSize="10"
                  fill="#9CA3AF"
                  textAnchor="middle"
                >
                  024
                </text>
                <text
                  x="340"
                  y="195"
                  fontSize="10"
                  fill="#9CA3AF"
                  textAnchor="middle"
                >
                  30
                </text>

                {/* Baseline (gray) line */}
                <path
                  d="M 40 160 L 100 150 L 160 140 L 220 135 L 280 130 L 340 125"
                  fill="none"
                  stroke="#D1D5DB"
                  strokeWidth="2"
                />

                {/* AI Performance (orange) line */}
                <path
                  d="M 40 150 L 100 120 L 160 90 L 220 60 L 280 40 L 340 25"
                  fill="none"
                  stroke="#F97316"
                  strokeWidth="3"
                />

                {/* Data points for orange line */}
                <circle cx="40" cy="150" r="3" fill="#F97316" />
                <circle cx="100" cy="120" r="3" fill="#F97316" />
                <circle cx="160" cy="90" r="3" fill="#F97316" />
                <circle cx="220" cy="60" r="3" fill="#F97316" />
                <circle cx="280" cy="40" r="3" fill="#F97316" />
                <circle cx="340" cy="25" r="3" fill="#F97316" />

                {/* Exponential Growth label */}
                <text
                  x="200"
                  y="75"
                  fontSize="11"
                  fill="#F97316"
                  fontWeight="500"
                >
                  Exponential Growth
                </text>

                {/* +5% improvement label */}
                <text
                  x="320"
                  y="15"
                  fontSize="10"
                  fill="#F97316"
                  fontWeight="500"
                >
                  +5% improvement
                </text>
                <line
                  x1="340"
                  y1="20"
                  x2="360"
                  y2="10"
                  stroke="#F97316"
                  strokeWidth="1"
                  markerEnd="url(#arrowhead)"
                />

                {/* Arrow marker */}
                <defs>
                  <marker
                    id="arrowhead"
                    markerWidth="10"
                    markerHeight="7"
                    refX="9"
                    refY="3.5"
                    orient="auto"
                  >
                    <polygon points="0 0, 10 3.5, 0 7" fill="#F97316" />
                  </marker>
                </defs>
              </svg>
            </div>

            <div className="chart-legend">
              <div className="legend-item">
                <div className="legend-dot orange"></div>
                <span>AI-Optimized Performance</span>
              </div>
              <div className="legend-item">
                <div className="legend-dot gray"></div>
                <span>Industry Average</span>
              </div>
            </div>

            <div className="budget-impact-section">
              <div className="impact-icon">
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M22 7L14 15L9 10L2 17"
                    stroke="#F97316"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M16 7H22V13"
                    stroke="#F97316"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </div>
              <div className="impact-content">
                <h5 className="impact-title">Budget Impact</h5>
                <p className="impact-description">
                  Our AI optimization delivers exponential performance gains as
                  your budget increases. Higher budgets enable more
                  sophisticated targeting and optimization strategies.
                </p>
              </div>
            </div>
          </section>
        </div>
      </div>

      {/* Additional Smart Mode Features */}
      <section className="additional-features-section">
        <div className="additional-features-header">
          <svg
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M21 10.5V19C21 19.5304 20.7893 20.0391 20.4142 20.4142C20.0391 20.7893 19.5304 21 19 21H5C4.46957 21 3.96086 20.7893 3.58579 20.4142C3.21071 20.0391 3 19.5304 3 19V5C3 4.46957 3.21071 3.96086 3.58579 3.58579C3.96086 3.21071 4.46957 3 5 3H17.5"
              stroke="#1a73e8"
              strokeWidth="2"
            />
            <path d="M9 11L12 14L22 4" stroke="#1a73e8" strokeWidth="2" />
          </svg>
          <div className="additional-features-title-group">
            <h3 className="additional-features-title">
              Additional Smart Mode Features
            </h3>
            <p className="additional-features-subtitle">
              More benefits of our AI-powered optimization
            </p>
          </div>
        </div>

        <div className="additional-features-grid">
          {additionalFeatures.map((feature, index) => (
            <div key={index} className="additional-feature-card">
              <div className="additional-feature-icon">{feature.icon}</div>
              <div className="additional-feature-content">
                <h4 className="additional-feature-title">{feature.title}</h4>
                <p className="additional-feature-description">
                  {feature.description}
                </p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* FAQ Section */}
      <section className="faq-section">
        <div className="faq-header">
          <svg
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <circle cx="12" cy="12" r="10" stroke="#1a73e8" strokeWidth="2" />
            <path
              d="M9.09 9C9.3251 8.33167 9.78915 7.76811 10.4 7.40913C11.0108 7.05016 11.7289 6.91894 12.4272 7.03871C13.1255 7.15849 13.7588 7.52152 14.2151 8.06353C14.6714 8.60553 14.9211 9.29152 14.92 10C14.92 12 11.92 13 11.92 13"
              stroke="#1a73e8"
              strokeWidth="2"
            />
            <path d="M12 17H12.01" stroke="#1a73e8" strokeWidth="2" />
          </svg>
          <h3 className="faq-title">Frequently Asked Questions</h3>
        </div>

        <div className="faq-items">
          {faqItems.map((item, index) => (
            <div key={index} className="faq-item">
              <button
                type="button"
                className="faq-question"
                onClick={() => toggleFaq(index)}
              >
                <span>{item.question}</span>
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  className={`faq-arrow ${expandedFaq === index ? "expanded" : ""}`}
                >
                  <path d="M6 9L12 15L18 9" stroke="#737373" strokeWidth="2" />
                </svg>
              </button>
              {expandedFaq === index && (
                <div className="faq-answer">
                  <p>{item.answer}</p>
                </div>
              )}
            </div>
          ))}
        </div>
      </section>

      {/* Navigation Buttons */}
      <div className="form-actions">
        <button type="button" className="back-button" onClick={handleBack}>
          Back
        </button>
        <button 
          type="button" 
          className={`next-button ${isLaunching ? 'launching' : ''}`}
          onClick={handleNext}
          disabled={isLaunching}
        >
          {isLaunching ? (
            <>
              <svg 
                width="16" 
                height="16" 
                viewBox="0 0 24 24" 
                fill="none" 
                xmlns="http://www.w3.org/2000/svg"
                style={{ marginRight: '8px', animation: 'spin 1s linear infinite' }}
              >
                <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" strokeOpacity="0.3"/>
                <path d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" fill="currentColor"/>
              </svg>
              Launching...
            </>
          ) : (
            'Launch Campaign'
          )}
        </button>
      </div>
    </form>
  );
};

export default CampaignBudget;
